/* eslint-disable max-len */
// Required for lazy loading
import {
    Alert,
    FaIcon, Hide, PageLayout, Show,
} from '@acp/core-ui';
import {
    useAgencySwitcher, useSession, useAgencySettings,
} from '@acp/feature/auth';
import {
    Button,
    Flex,
    HStack,
    Heading,
    Modal,
    Text,
    VStack,
    useDisclosure,
} from '@chakra-ui/react';
import { faBuilding, faUser } from '@fortawesome/pro-regular-svg-icons';
import { useState } from 'react';
import { isBefore } from 'date-fns';
import {
    ActionRequiredCard, SearchPropertyCard, DashboardGraphs, MarketingTiles,
    CardLayout,
} from '.';
import { SwitchPortfolioPanel } from '../../components/app-navigation-bar';
import { ImpersonationWarning } from './components/impersonation-warning';
import { PropertyManagerSelect } from '../agency';
import { usePortfolioMirroring, useUserSettings } from '../settings';
import { DashboardIntegratedGraphs } from './components/dashboard-graphs/integrated-graphs/dashboard-integrated-graphs';
import { NewWorkOrderButton, useAutoOpenPropertyRequestModal } from '../property-request';
import { ProperlyLinkBanner } from './components/properly-link-banner';
import { QldAutoIbtBanner } from './components/qld-auto-ibt-banner';

// Required for lazy loading
// eslint-disable-next-line import/no-default-export
export default function DashboardPage() {
    const { complianceUserName, complianceAgencyName } = useSession();
    const { userAgencies, isLoading } = useAgencySwitcher();
    const userSettings = useUserSettings();
    const portfolioMirroring = usePortfolioMirroring();
    const { agencySettings } = useAgencySettings();

    const [selectedPropertyManagerId, setSelectedPropertyManagerId] = useState<string | null>(
        userSettings.data?.agency.defaultPropertyManagerId?.toString() ?? null
    );

    const hasMultipleAgencies = !isLoading
        && userAgencies !== undefined
        && userAgencies.length > 1;

    const switchPortfolioDisclosure = useDisclosure();

    useAutoOpenPropertyRequestModal({
        queryParamName: 'show-work-order',
    });

    const hasAgreedToMinimumStandardsAssessments = agencySettings?.hasAgreedToMinimumStandardsAssessments;

    return (
        <VStack pt={[0, null, null, null, 8]} gap={[4, null, 8]}>
            <CardLayout bgColor="transparent">
                <HStack flexDir={['column', null, 'row']} alignItems="center" w="100%" gap={[5, null, 0]}>
                    <VStack flexGrow={1} alignItems="start" gap={[1, null, 2]} w="100%">
                        <Heading size="lg" fontFamily="Circular">
                            {`Hi ${complianceUserName}`}
                        </Heading>
                        <HStack flexDir={['column', null, 'row']} alignItems={['start', null, 'center']} gap={[1, null, 5]}>
                            <HStack color="charcoal.400" alignItems="center" gap={2}>
                                <FaIcon icon={faBuilding} pl={0.5} />
                                <Text fontSize="md">{complianceAgencyName}</Text>
                            </HStack>
                            <HStack color="charcoal.400" alignItems="center" gap={2}>
                                <FaIcon icon={faUser} pl={0.4} />
                                <Flex alignItems="center" cursor="pointer" h={8}>
                                    <PropertyManagerSelect
                                        onChange={(val) => setSelectedPropertyManagerId(val?.value ?? null)}
                                        value={selectedPropertyManagerId}
                                        isLabelDropdown
                                        isSearchable={false}
                                        placeholderColor="charcoal.400"
                                        placeholder="All property managers"
                                        hidePlaceholderWhileLoading
                                        stripUnknown
                                    />
                                </Flex>
                            </HStack>
                        </HStack>
                    </VStack>
                    <HStack alignItems={['start', null, 'center']} justifyContent={['start', null, 'end']} width={['100%', null, 'initial']}>
                        {
                            hasMultipleAgencies && (
                                <Button
                                    onClick={switchPortfolioDisclosure.onOpen}
                                    size={['cta-sm', null, 'cta-md']}
                                    variant="rounded-outline"
                                    colorScheme="charcoal"
                                >
                                    Switch portfolio
                                </Button>
                            )
                        }

                        <NewWorkOrderButton
                            size={['cta-sm', null, 'cta-md']}
                            variant="rounded-solid"
                            colorScheme="secondary"
                        >
                            New work order
                        </NewWorkOrderButton>
                    </HStack>
                </HStack>
            </CardLayout>

            {isBefore(new Date(), new Date('2025-03-23T14:00:00+11:00')) && (
                <CardLayout bgColor="transparent" px={0} py={0}>
                    <Alert
                        status="warning"
                        title="🚧 Scheduled Maintenance Notice 🚧"
                    >
                        <Text>
                            Our portal will be
                            {' '}
                            <b>temporarily unavailable</b>
                            {' '}
                            on
                            {' '}
                            <b>Sunday, March 23rd, from 6:00 AM - 4:00 PM (AEDT)</b>
                            {' '}
                            for
                            {' '}
                            <b>essential security and system updates</b>
                            . We appreciate your patience and understanding!
                        </Text>
                    </Alert>
                </CardLayout>
            )}

            <ImpersonationWarning />

            {hasAgreedToMinimumStandardsAssessments === true && (
                <ProperlyLinkBanner />
            )}

            {agencySettings?.eligibleForAutoLeaseRenewalActivation === true
            && userSettings.data?.user.hasDismissedQldAutoIbtBanner === false
                && (
                    <QldAutoIbtBanner />
                )}

            <SearchPropertyCard pageSize={5} />

            {portfolioMirroring.showFeature === true
                ? <DashboardIntegratedGraphs propertyManagerId={selectedPropertyManagerId} />
                : <DashboardGraphs propertyManagerId={selectedPropertyManagerId} />}

            <ActionRequiredCard propertyManagerId={selectedPropertyManagerId ?? undefined} />

            <MarketingTiles />

            <Hide above={PageLayout.Sidebar.Breakpoint}>
                {/* switch portfolio panel */}
                <Modal
                    isOpen={switchPortfolioDisclosure.isOpen}
                    onClose={switchPortfolioDisclosure.onClose}
                    size="full"
                >
                    <SwitchPortfolioPanel.Mobile
                        isOpen={switchPortfolioDisclosure.isOpen}
                        onClose={switchPortfolioDisclosure.onClose}
                    />
                </Modal>
            </Hide>
            <Show above={PageLayout.Sidebar.Breakpoint}>
                {/* switch portfolio panel */}
                <SwitchPortfolioPanel.Desktop
                    isOpen={switchPortfolioDisclosure.isOpen}
                    onClose={switchPortfolioDisclosure.onClose}
                />
            </Show>
        </VStack>
    );
}
